import { createRouter, createWebHistory } from "vue-router";
import HomeView from "../views/HomeView.vue";
import BrowseView from "../views/BrowseView.vue";
import SubmitView from "../views/SubmitView.vue";
import IdeaDetailView from "../views/IdeaDetailView.vue";
import ProfileView from "../views/ProfileView.vue";
import IdeaManagementView from "@/views/IdeaManagementView.vue";
import UserPoolView from "@/views/UserPoolView.vue";
import IdeaStatisticsView from "@/views/IdeaStatisticsView.vue";
import aiportalManageView from "@/views/AiPortalManageView.vue";
import WecomView from "@/views/WecomView.vue";
import PlaygroundView from "@/views/PlaygroundView.vue";
import CategoryManagementView from "@/views/CategoryManagementView.vue";
import TagManagementView from "@/views/TagManagementView.vue";

import { getUserInfo, fetchUserInfo, getAccessToken } from "@/state/UserInfo";
import { getSasToken } from "@/state/getSasToken";
import { checkIsAdmin } from "@/state/AdminState";
import axios from "axios";

// 创建 axios 实例用于权限检查
const baseURL =
  process.env.NODE_ENV === "development"
    ? "/"
    : process.env.VUE_APP_BASE_URL;

const axiosInstance = axios.create({
  baseURL,
});

// 添加请求拦截器，自动附加 token
axiosInstance.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const routes = [
  {
    path: "/wecom",
    name: "wecom",
    component: WecomView,
  },
  {
    path: "/login",
    name: "login",
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/LoginView.vue"),
  },
  {
    path: "/aiportal",
    name: "aiportal",
    component: () =>
      import(/* webpackChunkName: "about" */ "../views/AiPoptal.vue"),
  },
  {
    path: "/",
    name: "home",
    component: HomeView,
    meta: { requiresAuth: true },
  },
  {
    path: "/playground",
    name: "playground",
    component: PlaygroundView,
    meta: { requiresAuth: true },
  },
  {
    path: "/browse",
    name: "browse",
    component: BrowseView,
    meta: { requiresAuth: true },
  },
  {
    path: "/submit",
    name: "submit",
    component: SubmitView,
    meta: { requiresAuth: true },
  },
  {
    path: "/ideas/:id",
    name: "ideas",
    component: IdeaDetailView,
    meta: { requiresAuth: true },
  },
  {
    path: "/profile",
    name: "profile",
    component: ProfileView,
    meta: { requiresAuth: true },
  },
  {
    path: "/management",
    name: "management",
    component: IdeaManagementView,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/userpool",
    name: "userpool",
    component: UserPoolView,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/stastics",
    name: "ideaStatisticsView",
    component: IdeaStatisticsView,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/aiportalManage",
    name: "aiportalManageView",
    component: aiportalManageView,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/category",
    name: "categoryManagementView",
    component: CategoryManagementView,
  },
  {
    path: "/tag",
    name: "tagManagementView",
    component: TagManagementView,
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

interface TokenResult {
  access_token: string;
  expires_in: number;
  ext_expires_in: number;
  expire_date: number;
  scope: string;
  token_type: string;
}

function isTokenExpired(tokenResult: TokenResult): boolean {
  const currentTime = new Date().getTime();
  return currentTime > tokenResult.expire_date;
}
router.beforeEach(async (to, from, next) => {
  let userInfo = getUserInfo();
  const accessToken = getAccessToken();
  
  // 如果内存中没有用户信息，但 cookie 中有 token，尝试从 API 获取
  if (!userInfo && accessToken) {
    userInfo = await fetchUserInfo();
  }
  
  const isWeCom = /MicroMessenger/i.test(navigator.userAgent);
  const wecomUserId = userInfo?.wecom_userid;
  const hasRedirected = to.query.state;
  if (hasRedirected) {
    next();
  } else if (isWeCom && (!wecomUserId || wecomUserId === "")) {
    const weComRedirectUrl =
      "https://open.weixin.qq.com/connect/oauth2/authorize";
    const appId = "wxea15d639e2155786";
    const url = location.origin
    const redirectUri = encodeURIComponent(
      url + "/wecom"
    );
    const responseType = "code";
    const scope = "snsapi_base";
    const state = "redirected";

    const fullRedirectUrl = `${weComRedirectUrl}?appid=${appId}&redirect_uri=${redirectUri}&response_type=${responseType}&scope=${scope}&state=${state}#wechat_redirect`;

    window.location.href = fullRedirectUrl;
  } else if (to.matched.some((record) => record.meta.requiresAuth)) {
    // Get the user info from memory
    getSasToken();
    
    // 判断是否已登录：有 token 且有用户信息
    const isAuthenticated = accessToken && userInfo;
    
    // 如果有用户信息，检查 token 是否过期（如果有 tokenResult 的话）
    const isTokenValid = !userInfo?.tokenResult || !isTokenExpired(userInfo.tokenResult);
    
    if (isAuthenticated && isTokenValid) {
      if (to.matched.some((record) => record.meta.requiresAdmin)) {
        // 调用接口检查管理员权限
        const isAdmin = await checkIsAdmin(axiosInstance);
        if (isAdmin) {
          next(() => {
            window.scrollTo(0, 0);
          });
        } else {
          // If the user is not an admin, stop the navigation
          next({
            path: "/",
          });
        }
      } else {
        next(() => {
          window.scrollTo(0, 0);
        });
      }
    } else {
      // 没有 token 或无法获取用户信息，跳转到登录页
      next({
        path: "/login",
        query: { nextUrl: to.fullPath },
      });
    }
  } else {
    next(() => {
      window.scrollTo(0, 0);
    });
  }
});

export default router;
